/**
 * Modal del Admin (design-system §7 · Modal): 480 px para formularios, 640 px para detalle,
 * radio lg, padding 24 px, título Nunito 700 y acciones abajo a la derecha. Radix Dialog aporta
 * foco atrapado, Escape y `aria-modal`.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

import type { ReactNode } from 'react';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const OVERLAY_CLASSES = 'fixed inset-0 z-40 bg-fm-overlay';

export interface DialogContentProps {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** `form` = 480 px (por defecto), `detail` = 640 px. */
  size?: 'form' | 'detail';
  className?: string;
}

export function DialogContent({
  title,
  description,
  children,
  footer,
  size = 'form',
  className,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={OVERLAY_CLASSES} />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          'rounded-fm-lg border border-fm-border bg-fm-surface p-fm-6 shadow-fm-2',
          'transition-opacity duration-fm-slow ease-fm-emphasized motion-reduce:transition-none',
          size === 'detail' ? 'max-w-[640px]' : 'max-w-[480px]',
          className,
        )}
      >
        <DialogPrimitive.Title className="font-display text-fm-xl font-bold text-fm-text">
          {title}
        </DialogPrimitive.Title>
        {description ? (
          <DialogPrimitive.Description className="mt-fm-2 text-fm-sm text-fm-text-secondary">
            {description}
          </DialogPrimitive.Description>
        ) : null}

        <div className="mt-fm-4 text-fm-sm text-fm-text">{children}</div>

        {footer ? <div className="mt-fm-6 flex justify-end gap-fm-2">{footer}</div> : null}

        <DialogPrimitive.Close
          aria-label={t('common.buttons.close')}
          className="absolute right-fm-4 top-fm-4 inline-flex size-fm-touch items-center justify-center rounded-fm-md text-fm-text-secondary hover:bg-fm-bg-sunken focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
        >
          <X size={20} aria-hidden="true" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}
